// src/components/Gamification/GameResultScreen.jsx
import React, { useEffect, useState } from 'react';

/**
 * Post-game result ekranas (kaip Duolingo).
 * Props:
 *   score       – 0–100 (%)
 *   xpEarned    – XP gauta
 *   levelUp     – bool
 *   newLevel    – naujas lygis
 *   streak      – { streak_days, streak_broken }
 *   achievements – []
 *   onContinue  – callback "Tęsti"
 *   onPlayAgain – callback "Žaisti dar"
 *   gameName    – žaidimo pavadinimas
 */
export default function GameResultScreen({
  score = 0,
  xpEarned = 0,
  levelUp = false,
  newLevel = 1,
  streak = null,
  achievements = [],
  heartsRemaining = null,
  heartsLost = 0,
  onContinue,
  onPlayAgain,
  gameName = 'Žaidimas',
}) {
  const [starsVisible, setStarsVisible] = useState([false, false, false]);
  const [xpVisible, setXpVisible] = useState(false);

  const starCount = score >= 90 ? 3 : score >= 60 ? 2 : 1;

  useEffect(() => {
    // Animuoti žvaigždes po vieną
    [0, 1, 2].forEach(i => {
      setTimeout(() => {
        if (i < starCount) {
          setStarsVisible(prev => {
            const n = [...prev];
            n[i] = true;
            return n;
          });
        }
      }, 300 + i * 400);
    });

    setTimeout(() => setXpVisible(true), 1500);
  }, [starCount]);

  const scoreColor =
    score >= 90 ? '#58CC02' :
    score >= 60 ? '#1CB0F6' :
    '#FF9600';

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #f0fff0 0%, #e8f7ff 50%, #fff0f8 100%)' }}>

      <div className="w-full max-w-md">
        {/* Level Up banner */}
        {levelUp && (
          <div
            className="mb-6 px-6 py-4 rounded-3xl text-center text-white font-black text-xl animate-level-up shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #FFC800, #FF9600)' }}
          >
            🎉 LYGIS {newLevel}! Sveikiname!
          </div>
        )}

        {/* Main card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">

          {/* Žaidimo pavadinimas */}
          <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{gameName}</p>

          {/* Žvaigždės */}
          <div className="flex justify-center gap-3 mb-6">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="result-star"
                style={{
                  opacity: starsVisible[i] ? 1 : 0.15,
                  transform: starsVisible[i] ? 'scale(1)' : 'scale(0.5)',
                  transition: `all 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.1}s`,
                  filter: starsVisible[i] && i < starCount
                    ? 'drop-shadow(0 4px 8px rgba(255,200,0,0.5))'
                    : 'grayscale(1)',
                }}
              >
                ⭐
              </span>
            ))}
          </div>

          {/* Rezultatas */}
          <div
            className="text-6xl font-black mb-2"
            style={{ color: scoreColor }}
          >
            {score}%
          </div>
          <p className="text-gray-500 font-bold mb-6">
            {score >= 90 ? '🔥 Nuostabu!' :
             score >= 60 ? '👍 Gerai padaryta!' :
             '💪 Bandyk dar kartą!'}
          </p>

          {/* XP */}
          {xpVisible && xpEarned > 0 && (
            <div
              className="flex items-center justify-center gap-2 mb-6 animate-bounce-in"
            >
              <div
                className="px-5 py-2.5 rounded-full text-white font-black text-lg shadow-lg"
                style={{ background: 'linear-gradient(90deg, #58CC02, #89E219)' }}
              >
                +{xpEarned} XP
              </div>
            </div>
          )}

          {/* Streak */}
          {streak && !streak.streak_broken && (
            <div className="flex items-center justify-center gap-2 mb-4 text-orange-500 font-black">
              🔥 {streak.streak_days} dienų serija!
              {streak.is_new_record && <span className="text-xs bg-orange-100 px-2 py-0.5 rounded-full">Rekordas!</span>}
            </div>
          )}

          {streak?.streak_broken && (
            <div className="mb-4 text-gray-400 text-sm font-bold">
              Serija nutrūko. Pradėk iš naujo šiandien! 💪
            </div>
          )}

          {/* Hearts status */}
          {heartsRemaining !== null && (
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className={`text-2xl transition-all duration-300 ${
                    i < heartsRemaining ? '' : 'grayscale opacity-30'
                  } ${i >= heartsRemaining && i < heartsRemaining + heartsLost ? 'animate-heart-break' : ''}`}
                >
                  ❤️
                </span>
              ))}
              {heartsLost > 0 && (
                <span className="text-xs font-bold text-red-400 ml-2">-{heartsLost}</span>
              )}
            </div>
          )}

          {/* Achievements */}
          {achievements.length > 0 && (
            <div className="mb-6 p-3 bg-yellow-50 rounded-2xl border border-yellow-200">
              <p className="text-xs font-black text-yellow-600 mb-2">🏅 Nauja pasiekimai!</p>
              {achievements.map(a => (
                <div key={a.id} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <span>{a.icon}</span>
                  <span>{a.name}</span>
                  <span className="ml-auto text-green-600">+{a.xp_reward} XP</span>
                </div>
              ))}
            </div>
          )}

          {/* CTA mygtukai */}
          <div className="flex flex-col gap-3">
            <button
              onClick={onContinue}
              className="btn btn-green w-full text-lg"
              style={{
                background: 'var(--duo-green)',
                borderBottom: '4px solid var(--duo-green-dark)',
                color: 'white',
              }}
            >
              Tęsti →
            </button>
            <button
              onClick={onPlayAgain}
              className="btn btn-ghost w-full"
            >
              🔄 Žaisti dar kartą
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
